import { createContext, useContext, useEffect } from 'react';

/**
 * Lets the page currently on screen ask the gate not to swap it out yet.
 *
 * The gate resolves which page to render from the shift's status, so an action
 * that changes that status swaps the page under the office's feet. That is right
 * for the shift itself and wrong for what the action left on screen: confirming
 * a candidate opens "would you like to invite them for other shifts?", then
 * flips the shift to `filled` — and the page that owns the popup unmounts before
 * anyone can answer it (GOTU-7381 found the same shape on the invite list).
 *
 * So a page holds its slot while it is showing something that has to outlive the
 * status change, and releases it when that thing is gone or the page unmounts.
 * The shift's data keeps updating underneath either way — only the *choice of
 * page* is pinned, and only for as long as somebody is holding it.
 */
type THoldShiftDetailsPage = (isHeld: boolean) => void;

const ShiftDetailsPageHoldContext = createContext<THoldShiftDetailsPage>(() => {});

export const ShiftDetailsPageHoldProvider = ShiftDetailsPageHoldContext.Provider;

/**
 * Holds the page's slot for as long as `isHeld` is true.
 *
 * Declarative rather than a pair of `hold()`/`release()` calls, because the
 * failure mode of the imperative version is an office stranded on a page the
 * shift left behind. Tied to the render like this, the hold cannot outlive the
 * condition that asked for it, and unmounting always releases.
 *
 * Outside the gate — the legacy screens, or the flag-off path — the default is a
 * no-op, so a page is safe to call this wherever it is rendered.
 */
export const useHoldShiftDetailsPage = (isHeld: boolean) => {
  const holdPage = useContext(ShiftDetailsPageHoldContext);

  useEffect(() => {
    holdPage(isHeld);

    return () => holdPage(false);
  }, [holdPage, isHeld]);
};
